import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, TextField, FormControl, InputLabel, Select, MenuItem, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack
} from '@mui/material';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  People as UsersIcon,
  Settings as SettingsIcon,
  Science as ScienceIcon,
  BugReport as DebugIcon,
  Info as InfoIcon,
  PersonAdd as AddUserIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  SystemUpdateAlt as OtaIcon,
  StopCircle as StopIcon
} from '@mui/icons-material';
import SortableTable from '../components/SortableTable';
import FirmwareOta from '../components/FirmwareOta';
import DeviceSandbox from '../components/DeviceSandbox';
import api from '../api/axios';
import { useDeviceSettings } from '../context/DeviceSettingsContext';
import { useDeviceSelection } from '../context/DeviceSelectionContext';
import { useAuth } from '../auth/AuthContext';

// Stable empty-settings reference so memo/effect deps don't churn on every render.
const EMPTY_SETTINGS = {};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}


function AdminPanel() {
  const { role } = useAuth();
  // --- Geolocation for device registration ---
  const [geoLoading, setGeoLoading] = useState(false);
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser.');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude.toFixed(6));
        setLongitude(position.coords.longitude.toFixed(6));
        setGeoLoading(false);
      },
      (error) => {
        let errorMsg = 'Unable to retrieve your location. ';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMsg += 'Permission denied. Please allow location access in your browser.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMsg += 'Location request timed out.';
            break;
          default:
            errorMsg += 'An unknown error occurred.';
        }
        alert(errorMsg);
        console.error('Geolocation error:', error);
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  // --- Mobile-friendly utility ---
  const isMobile = window.innerWidth <= 600;

  // --- SWP Master State & Columns ---
  const [swpMasterRows, setSwpMasterRows] = useState([]);
  const swpMasterColumns = [
    { id: 'symbol', label: 'Symbol', width: 80, editable: true },
    { id: 'name', label: 'Name', width: 120, editable: true },
    { id: 'quantity', label: 'Quantity', width: 100, editable: true },
  ];
  useEffect(() => {
    setSwpMasterRows([
      { symbol: 'Li', name: 'Lithium', quantity: 100 },
      { symbol: 'Ca', name: 'Calcium', quantity: 100 },
      { symbol: 'Na', name: 'Sodium', quantity: 100 },
      { symbol: 'Cl', name: 'Chlorine', quantity: 100 },
      { symbol: 'K', name: 'Potassium', quantity: 100 },
      { symbol: 'Mg', name: 'Magnesium', quantity: 100 },
      { symbol: 'Fe', name: 'Iron', quantity: 100 },
      { symbol: 'Cu', name: 'Copper', quantity: 100 },
      { symbol: 'Zn', name: 'Zinc', quantity: 100 },
      { symbol: 'Mn', name: 'Manganese', quantity: 100 },
      { symbol: 'Pb', name: 'Lead', quantity: 100 },
      { symbol: 'Ni', name: 'Nickel', quantity: 100 },
      { symbol: 'Co', name: 'Cobalt', quantity: 100 },
      { symbol: 'Ag', name: 'Silver', quantity: 100 },
      { symbol: 'Hg', name: 'Mercury', quantity: 100 },
      { symbol: 'Al', name: 'Aluminum', quantity: 100 },
      { symbol: 'Cr', name: 'Chromium', quantity: 100 },
      { symbol: 'Ba', name: 'Barium', quantity: 100 },
      { symbol: 'Sr', name: 'Strontium', quantity: 100 },
      { symbol: 'Cd', name: 'Cadmium', quantity: 100 },
      { symbol: 'Sn', name: 'Tin', quantity: 100 },
      { symbol: 'Sb', name: 'Antimony', quantity: 100 },
      { symbol: 'Se', name: 'Selenium', quantity: 100 },
      { symbol: 'Mo', name: 'Molybdenum', quantity: 100 },
      { symbol: 'Ti', name: 'Titanium', quantity: 100 },
      { symbol: 'V', name: 'Vanadium', quantity: 100 },
      { symbol: 'W', name: 'Tungsten', quantity: 100 },
      { symbol: 'U', name: 'Uranium', quantity: 100 },
    ]);
  }, []);

  // --- State and hooks ---
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [registeredDevices, setRegisteredDevices] = useState([]);
  const [onlineDevices, setOnlineDevices] = useState([]);
  const [registeredDeviceIds, setRegisteredDeviceIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [customName, setCustomName] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationOptions, setLocationOptions] = useState([]);
  const [geoResolving, setGeoResolving] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserSaving, setAddUserSaving] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'user' });
  const [addUserError, setAddUserError] = useState('');
  const roleOptions = role === 'superadmin' ? ['user', 'admin', 'superadmin'] : ['user', 'admin'];
  const openAddUser = () => { setNewUser({ username: '', email: '', password: '', role: 'user' }); setAddUserError(''); setError(''); setSuccess(''); setAddUserOpen(true); };
  const handleCreateUser = async () => {
    const { username, email, password } = newUser;
    if (!username || !email || !password) { setAddUserError('Username, email and password are required.'); return; }
    if (password.length < 8) { setAddUserError('Password must be at least 8 characters.'); return; }
    setAddUserError(''); setSuccess(''); setAddUserSaving(true);
    try {
      await api.post('/users', newUser);
      const res = await api.get('/users');
      setUsers(Array.isArray(res.data) ? res.data : []);
      setSuccess('User created successfully.');
      setAddUserOpen(false);
    } catch (err) {
      setAddUserError(err.response?.data?.message || 'Failed to create user.');
    } finally {
      setAddUserSaving(false);
    }
  };
  const swpFileInputRef = useRef();
  const VOUT_VIS_KEY = 'allowUserVoutView';
  const [allowUserVoutView, setAllowUserVoutView] = useState(() => {
    const stored = localStorage.getItem(VOUT_VIS_KEY);
    if (stored === null || stored === undefined) return true;
    return stored === 'true';
  });

  // --- Device Settings State & Columns ---
  const { selectedDeviceId } = useDeviceSelection();
  const { settingsByDevice, fetchDeviceSettings, updateDeviceSettings } = useDeviceSettings();
  // Settings shown are always scoped to the selected device (fleet-safe). Memoized so
  // the empty-state reference is stable and dependent effects don't loop.
  const deviceSettings = useMemo(
    () => settingsByDevice[selectedDeviceId] || EMPTY_SETTINGS,
    [settingsByDevice, selectedDeviceId]
  );
  // Auto-refetch only while the Device Settings tab is open, so simply visiting
  // the Admin Panel (e.g. the Users tab) doesn't fire a settings read at a device
  // that may be offline — which would otherwise pile up "Failed" History entries.
  useEffect(() => {
    if (selectedDeviceId && activeTab === 1) fetchDeviceSettings(selectedDeviceId);
  }, [selectedDeviceId, activeTab, fetchDeviceSettings]);
  const [voutRows, setVoutRows] = useState([]);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const deviceSettingsColumns = [
    { id: 'setting', label: 'Setting', width: 160 },
    { 
      id: 'value', 
      label: 'Value', 
      width: 240,
      editable: true,
      render: (v) => {
        if (v === null || v === undefined) return 'N/A';
        if (Array.isArray(v)) return JSON.stringify(v);
        if (typeof v === 'object') return JSON.stringify(v);
        return v;
      }
    },
  ];

  // Normalize device settings rows and hide elements payload from the main table
  const deviceSettingsData = deviceSettings
    ? (Array.isArray(deviceSettings)
        ? deviceSettings
        : Object.entries(deviceSettings).map(([setting, value]) => ({ setting, value })))
        .filter(row => row.setting !== 'elements')
    : [];

  // --- Emergency stop ---
  const [estopBusy, setEstopBusy] = useState(false);
  const [estopConfirmOpen, setEstopConfirmOpen] = useState(false);
  const handleEmergencyStop = async () => {
    if (!selectedDeviceId) { setError('Please select a device first'); return; }
    setError(''); setSuccess(''); setEstopBusy(true);
    try {
      const res = await api.post('/device/emergency-stop', { deviceId: selectedDeviceId });
      if (res.data?.success) {
        setSuccess(`Emergency stop sent to ${selectedDeviceId}. The device is halting the cycle and returning to a safe state.`);
      } else {
        setError(res.data?.message || 'Emergency stop could not be delivered — the device may be offline.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to send emergency stop.');
    } finally {
      setEstopBusy(false);
      setEstopConfirmOpen(false);
    }
  };

  const handleSendDeviceSettings = async () => {
    if (!selectedDeviceId) {
      setError('Please select a device first');
      return;
    }
    setError('');
    setSuccess('');
    setSettingsSaving(true);
    try {
      const baseSettings = Array.isArray(deviceSettings)
        ? Object.fromEntries(deviceSettings.map(row => [row.setting, row.value]))
        : { ...deviceSettings };
      const payload = {
        ...baseSettings,
        vout_table: voutRows,
        deviceId: selectedDeviceId
      };
      await updateDeviceSettings(payload, selectedDeviceId);
      setSuccess('Device settings sent to device successfully.');
    } catch (err) {
      setError('Failed to send device settings to device.');
    } finally {
      setSettingsSaving(false);
    }
  };
  const voutColumns = [
    { id: 'symbol', label: 'Symbol', width: 80 },
    { id: 'name', label: 'Name', width: 120 },
    { id: 'vout_base', label: 'Vout Base', width: 100 },
    { id: 'freq', label: 'Frequency', width: 100 },
  ];

  // --- Vout Table Data (28 elements) ---
  useEffect(() => {
    // If deviceSettings has elements array from fetch, use it
    if (deviceSettings && deviceSettings.elements && Array.isArray(deviceSettings.elements)) {
      setVoutRows(deviceSettings.elements.map(el => ({
        symbol: el.symbol || '',
        name: el.name || '', // STM32 doesn't send names, so this will be empty
        vout_base: el.vout_base || 0,
        freq: el.freq || 0
      })));
    } else {
      // Default hardcoded data if no elements fetched yet
      setVoutRows([
        { symbol: 'Li', name: 'Lithium', vout_base: 3.71, freq: 2226 },
        { symbol: 'Ca', name: 'Calcium', vout_base: 2.95, freq: 1765 },
        { symbol: 'Na', name: 'Sodium', vout_base: 3.032, freq: 1818 },
        { symbol: 'Cl', name: 'Chlorine', vout_base: 3.916, freq: 2351 },
        { symbol: 'K', name: 'Potassium', vout_base: 2.98, freq: 1760 },
        { symbol: 'Mg', name: 'Magnesium', vout_base: 2.56, freq: 1527 },
        { symbol: 'Fe', name: 'Iron', vout_base: 2.23, freq: 1330 },
        { symbol: 'Cu', name: 'Copper', vout_base: 2.01, freq: 1200 },
        { symbol: 'Zn', name: 'Zinc', vout_base: 2.37, freq: 1410 },
        { symbol: 'Mn', name: 'Manganese', vout_base: 2.15, freq: 1280 },
        { symbol: 'Pb', name: 'Lead', vout_base: 1.98, freq: 1180 },
        { symbol: 'Ni', name: 'Nickel', vout_base: 2.05, freq: 1220 },
        { symbol: 'Co', name: 'Cobalt', vout_base: 2.10, freq: 1250 },
        { symbol: 'Ag', name: 'Silver', vout_base: 1.80, freq: 1080 },
        { symbol: 'Hg', name: 'Mercury', vout_base: 1.76, freq: 1060 },
        { symbol: 'Al', name: 'Aluminum', vout_base: 2.20, freq: 1320 },
        { symbol: 'Cr', name: 'Chromium', vout_base: 2.12, freq: 1260 },
        { symbol: 'Ba', name: 'Barium', vout_base: 2.90, freq: 1740 },
        { symbol: 'Sr', name: 'Strontium', vout_base: 2.85, freq: 1710 },
        { symbol: 'Cd', name: 'Cadmium', vout_base: 2.00, freq: 1200 },
        { symbol: 'Sn', name: 'Tin', vout_base: 1.95, freq: 1170 },
        { symbol: 'Sb', name: 'Antimony', vout_base: 1.92, freq: 1150 },
        { symbol: 'Se', name: 'Selenium', vout_base: 1.90, freq: 1140 },
        { symbol: 'Mo', name: 'Molybdenum', vout_base: 2.30, freq: 1380 },
        { symbol: 'Ti', name: 'Titanium', vout_base: 2.25, freq: 1350 },
        { symbol: 'V', name: 'Vanadium', vout_base: 2.18, freq: 1310 },
        { symbol: 'W', name: 'Tungsten', vout_base: 2.40, freq: 1420 },
        { symbol: 'U', name: 'Uranium', vout_base: 2.50, freq: 1480 },
      ]);
    }
  }, [deviceSettings]);

  // --- Device Settings Handler now provided by context ---

  // --- Table columns ---
  const userColumns = [
    { id: 'username', label: 'Username', width: 120 },
    { id: 'email', label: 'Email', width: 180, editable: true },
    { id: 'role', label: 'Role', width: 80, editable: true, type: 'select', options: [
      { value: 'user', label: 'User' },
      { value: 'admin', label: 'Admin' },
    ], render: (v, row) => row.role },
    { id: 'active', label: 'Active', width: 80, editable: (row) => row.role !== 'superadmin', type: 'select', options: [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' },
    ], render: (v, row) => row.active ? 'Active' : 'Inactive' },
  ];
  const registeredDeviceColumns = [
    { id: 'deviceId', label: 'Device ID', width: 120 },
    { id: 'customName', label: 'Custom Name', width: 120 },
    { id: 'locationName', label: 'Location', width: 160, render: (v) => v || '—' },
    { id: 'stmFirmwareVersion', label: 'Device FW', width: 120, render: (v, row) => v ? (row.buildCode != null ? `${v} (build ${row.buildCode})` : v) : '—' },
    { id: 'espBridgeVersion', label: 'Bridge FW', width: 100, render: (v) => v || '—' },
    { id: 'latitude', label: 'Latitude', width: 80 },
    { id: 'longitude', label: 'Longitude', width: 80 },
  ];
  // Removed unused swp/debug column definitions

  // --- Data fetching ---
  useEffect(() => {
    // Fetch users
  api.get('/users').then(res => setUsers(res.data)).catch(() => {});
    // Fetch registered devices
  api.get('/device/devices/registered').then(res => {
      console.log('🔍 Registered devices response:', res.data); // Debug log
      setRegisteredDevices(res.data);
      setRegisteredDeviceIds(res.data.map(d => d.deviceId));
    }).catch(() => {});
    // Fetch online devices
  api.get('/device/devices/online').then(res => setOnlineDevices(res.data)).catch(() => {});
    // Removed fetching of SWP/debug rows used by unused tables
  }, []);

  // When coordinates are entered, ask the backend for candidate area/city labels so
  // the admin can pick the correct one (stored with the device on registration).
  useEffect(() => {
    if (!latitude || !longitude) {
      setLocationOptions([]);
      setLocationName('');
      return;
    }
    let cancelled = false;
    setGeoResolving(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/device/geocode', { params: { lat: latitude, lng: longitude } });
        if (cancelled) return;
        const opts = Array.isArray(res.data?.options) ? res.data.options : [];
        setLocationOptions(opts);
        setLocationName(opts[0] || '');
      } catch {
        if (!cancelled) { setLocationOptions([]); setLocationName(''); }
      } finally {
        if (!cancelled) setGeoResolving(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [latitude, longitude]);

  useEffect(() => {
    localStorage.setItem(VOUT_VIS_KEY, allowUserVoutView ? 'true' : 'false');
  }, [allowUserVoutView]);

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>Admin Panel</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} aria-label="admin panel tabs">
          <Tab label="Users" icon={<UsersIcon />} iconPosition="start" />
          <Tab label="Device Settings" icon={<SettingsIcon />} iconPosition="start" />
          <Tab label="SWP Master" icon={<ScienceIcon />} iconPosition="start" />
          <Tab label="Sandbox" icon={<DebugIcon />} iconPosition="start" />
          <Tab label="Register Device" icon={<InfoIcon />} iconPosition="start" />
          {role === 'superadmin' && <Tab label="Firmware / OTA" icon={<OtaIcon />} iconPosition="start" />}
        </Tabs>
      </Paper>
      <TabPanel value={activeTab} index={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Users</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {role === 'superadmin' && (
              <FormControlLabel
                control={<Switch checked={allowUserVoutView} onChange={(e) => setAllowUserVoutView(e.target.checked)} />}
                label="Allow users to view Vout/Freq"
              />
            )}
            <Button variant="contained" startIcon={<AddUserIcon />} onClick={openAddUser}>Add User</Button>
          </Box>
        </Box>
        <SortableTable
          columns={userColumns}
          data={users}
          showPagination={true}
          editable={true}
          onEdit={async (rowIdx, colId, value) => {
            const user = users[rowIdx];
            const userId = user.id;
            if (colId === 'role' && user.role === 'superadmin') {
              setError('Cannot change role of another superadmin.');
              return;
            }
            if (colId === 'active' && user.role === 'superadmin') {
              setError('Cannot change active status of a superadmin.');
              return;
            }
            try {
              await api.put(`/api/users/${userId}`, { [colId]: value });
              setUsers(prev => prev.map((u, i) => i === rowIdx ? { ...u, [colId]: value } : u));
              setSuccess('User updated successfully.');
            } catch (err) {
              setError('Failed to update user.');
            }
          }}
          onDelete={async (rowIdx) => {
            const user = users[rowIdx];
            const userId = user.id;
            if (user.role === 'superadmin') {
              setError('Cannot delete a superadmin.');
              return;
            }
            if (!window.confirm(`Delete user ${user.username}?`)) return;
            try {
              await api.delete(`/api/users/${userId}`);
              setUsers(prev => prev.filter((_, i) => i !== rowIdx));
              setSuccess('User deleted successfully.');
            } catch (err) {
              setError('Failed to delete user.');
            }
          }}
          permissions={{
            canEdit: true,
            canDelete: true,
            canAdd: false
          }}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, borderRadius: 1,
          border: '1px solid', borderColor: 'error.main', bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(211,47,47,0.12)' : 'rgba(211,47,47,0.06)' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'error.main' }}>Emergency Stop</Typography>
            <Typography variant="caption" color="text.secondary">
              Immediately halts the current cycle on the selected device (even mid-cycle) and returns it to a safe idle state.
            </Typography>
          </Box>
          <Button
            variant="contained" color="error" size="large" startIcon={<StopIcon />}
            disabled={!selectedDeviceId || estopBusy}
            onClick={() => setEstopConfirmOpen(true)}
          >
            {estopBusy ? 'Stopping…' : 'Emergency Stop'}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">Device Settings</Typography>
            <Typography variant="caption" color={selectedDeviceId ? 'text.secondary' : 'error'}>
              {selectedDeviceId ? `Target device: ${selectedDeviceId}` : 'No device selected — choose one in the top bar'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={() => fetchDeviceSettings(selectedDeviceId)} disabled={!selectedDeviceId}>Fetch Device Settings</Button>
            <Button variant="contained" onClick={handleSendDeviceSettings} disabled={settingsSaving || !selectedDeviceId}>
              {settingsSaving ? 'Sending…' : 'Send to Device'}
            </Button>
          </Box>
        </Box>
        <SortableTable
          columns={deviceSettingsColumns}
          data={deviceSettingsData}
          showPagination={true}
          defaultRowsPerPage={isMobile ? 3 : 5}
          editable={true}
          onEdit={async (rowIdx, colId, value) => {
            // Convert array to object if needed; guard against null/undefined
            if (!deviceSettings) return;
            let settingsObj = Array.isArray(deviceSettings)
              ? Object.fromEntries(deviceSettings.map(row => [row.setting, row.value]))
              : { ...deviceSettings };
            const keys = Object.keys(settingsObj).filter(k => k !== 'elements');
            const settingKey = keys[rowIdx];
            if (settingKey) {
              settingsObj[settingKey] = value;
              await updateDeviceSettings(settingsObj, selectedDeviceId);
            }
          }}
        />
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Vout Table</Typography>
            <Button variant="contained" sx={{ ml: 2 }} onClick={async () => {
              if (!selectedDeviceId) {
                setError('Please select a device first');
                return;
              }
              try {
                await api.post('/device/vout-table', voutRows, { params: { deviceId: selectedDeviceId } });
                setSuccess('Vout table sent to device and saved to flash.');
              } catch (err) {
                setError('Failed to send vout table: ' + (err.response?.data?.error || err.message));
              }
            }}>Set Vout Table for Device</Button>
          </Box>
          <SortableTable
            columns={voutColumns.map(col => ({ ...col, editable: col.id === 'vout_base' || col.id === 'freq' }))}
            data={voutRows}
            showPagination={true}
            defaultRowsPerPage={isMobile ? 3 : 5}
            editable={true}
            onEdit={(rowIdx, colId, value) => {
              setVoutRows(prev => prev.map((row, idx) => idx === rowIdx ? { ...row, [colId]: value } : row));
            }}
          />
        </Box>
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>SWP Master</Typography>
          <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ mr: 1 }}>Download Template</Button>
          <Button variant="outlined" startIcon={<UploadIcon />} component="label">
            Upload SWP File
            <input type="file" hidden ref={swpFileInputRef} />
          </Button>
        </Box>
        <SortableTable
          columns={swpMasterColumns}
          data={swpMasterRows}
          showPagination={true}
          defaultRowsPerPage={isMobile ? 3 : 5}
          editable={true}
          onEdit={(rowIdx, colId, value) => {
            setSwpMasterRows(prev => prev.map((row, idx) =>
              idx === rowIdx ? { ...row, [colId]: colId === 'quantity' ? Number(value) : value } : row
            ));
          }}
        />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <DeviceSandbox />
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        <Typography variant="h6" gutterBottom>Register Device</Typography>
        <Box component="form" sx={{ maxWidth: 400 }}>
          <Button
            variant="outlined"
            onClick={handleGetLocation}
            sx={{ mb: 2 }}
            disabled={geoLoading}
          >
            {geoLoading ? 'Getting Location...' : 'Use My Current Location'}
          </Button>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Device ID</InputLabel>
            <Select value={deviceId} onChange={e => setDeviceId(e.target.value)} label="Device ID">
              {!Array.isArray(onlineDevices) || onlineDevices.length === 0 ? (
                <MenuItem value="" disabled>No online devices</MenuItem>
              ) : (
                onlineDevices.map(id => (
                  <MenuItem key={id} value={id} disabled={registeredDeviceIds.includes(id)}>
                    {id} {registeredDeviceIds.includes(id) ? '(Registered)' : ''}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <TextField label="Custom Name" value={customName} onChange={e => setCustomName(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <TextField label="Latitude" type="number" value={latitude || ''} onChange={e => setLatitude(e.target.value)} fullWidth sx={{ mb: 2 }} />
          <Box sx={{ mb: 2 }}>
            <TextField label="Longitude" type="number" value={longitude || ''} onChange={e => setLongitude(e.target.value)} fullWidth />
          </Box>
          {(latitude && longitude) && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Location (area / city)</InputLabel>
              <Select
                value={locationName}
                label="Location (area / city)"
                onChange={e => setLocationName(e.target.value)}
                displayEmpty
                renderValue={(val) => val || (geoResolving ? 'Finding locations…' : 'No location selected')}
              >
                {geoResolving && <MenuItem value="" disabled>Finding locations…</MenuItem>}
                {!geoResolving && locationOptions.length === 0 && <MenuItem value="" disabled>No suggestions found</MenuItem>}
                {locationOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
              </Select>
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                Pick the closest match — it's stored with the device.
              </Typography>
            </FormControl>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={loading}
            onClick={async (e) => {
              e.preventDefault();
              setError('');
              setSuccess('');
              if (!deviceId || !customName || !latitude || !longitude) {
                setError('All fields are required.');
                return;
              }
              setLoading(true);
              try {
                const res = await api.post('/device/devices/register', {
                  deviceId,
                  customName,
                  latitude,
                  longitude,
                  locationName,
                });
                setRegisteredDevices(prev => [...prev, res.data]);
                setRegisteredDeviceIds(prev => [...prev, deviceId]);
                setSuccess('Device registered successfully.');
                setDeviceId('');
                setCustomName('');
                setLatitude('');
                setLongitude('');
                setLocationName('');
                setLocationOptions([]);
              } catch (err) {
                setError('Failed to register device.');
              } finally {
                setLoading(false);
              }
            }}
          >
            {loading ? 'Registering…' : 'Register Device'}
          </Button>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1">Registered Devices</Typography>
          <SortableTable
            columns={[
              ...registeredDeviceColumns,
              {
                id: 'actions',
                label: 'Actions',
                width: 100,
                render: (v, row) => (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={async () => {
                      console.log('🔍 Row data:', row); // Debug log
                      console.log('🔍 Row ID:', row.id); // Debug log
                      console.log('🔍 Row _id:', row._id); // Debug log
                      console.log('🔍 Available keys:', Object.keys(row)); // Debug log
                      if (window.confirm(`Delete registration for device ${row.deviceId}?`)) {
                        setLoading(true);
                        setError('');
                        setSuccess('');
                        try {
                          // Try using the deviceId field temporarily to see what happens
                          const deleteId = row.id || row._id || row.deviceId;
                          console.log('🔍 Using delete ID:', deleteId);
                          await api.delete(`/api/device/devices/register/${deleteId}`);
                          setRegisteredDevices(prev => prev.filter(d => d.deviceId !== row.deviceId));
                          setRegisteredDeviceIds(prev => prev.filter(id => id !== row.deviceId));
                          setSuccess('Device registration deleted.');
                        } catch (err) {
                          console.error('🔍 Delete error:', err);
                          setError('Failed to delete device registration.');
                        } finally {
                          setLoading(false);
                        }
                      }
                    }}
                  >
                    Delete
                  </Button>
                ),
              },
            ]}
            data={registeredDevices}
            showPagination={true}
          />
        </Box>
      </TabPanel>
      {role === 'superadmin' && (
        <TabPanel value={activeTab} index={5}>
          <FirmwareOta />
        </TabPanel>
      )}

      <Dialog open={estopConfirmOpen} onClose={() => !estopBusy && setEstopConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>Confirm Emergency Stop</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will immediately stop the current cycle on <strong>{selectedDeviceId || 'the selected device'}</strong>,
            even if it is mid-cycle, and return it to a safe idle state.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            Any in-progress measurement will be aborted. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEstopConfirmOpen(false)} disabled={estopBusy}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<StopIcon />} onClick={handleEmergencyStop} disabled={estopBusy}>
            {estopBusy ? 'Stopping…' : 'Stop Now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addUserOpen} onClose={() => { setAddUserError(''); setAddUserOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {addUserError && <Alert severity="error" onClose={() => setAddUserError('')}>{addUserError}</Alert>}
            <TextField label="Username" value={newUser.username} fullWidth autoFocus
              onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} />
            <TextField label="Email" type="email" value={newUser.email} fullWidth
              onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} />
            <TextField label="Password" type="password" value={newUser.password} fullWidth
              helperText="At least 8 characters"
              onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={newUser.role} label="Role" onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}>
                {roleOptions.map(r => <MenuItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddUserError(''); setAddUserOpen(false); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateUser} disabled={addUserSaving}>
            {addUserSaving ? 'Creating…' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminPanel;

